import { useState } from "react";
import { Download, Info } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

type PdfFallbackNoticeProps = {
  /** Object URL or file URL the document can be saved from. */
  downloadUrl: string;
  downloadFileName?: string;
  /** Trims the copy for modals and preview panes, where height is scarce. */
  compact?: boolean;
};

/**
 * Shown wherever the browser's own PDF viewer cannot be used and the
 * application renders the document instead.
 *
 * Shared by all three PDF surfaces so the wording stays identical — see
 * `selectPdfViewerMode()` in `@/lib/pdf-viewer-support` for who shows it.
 */
export function PdfFallbackNotice({
  downloadUrl,
  downloadFileName = "document.pdf",
  compact = false,
}: PdfFallbackNoticeProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  /*
   * Clearing the class and re-adding it across two frames is what lets the
   * animation replay on a second click -- re-setting a flag that is already
   * true does not restart a running CSS animation. Same approach as the ID
   * card's download icon.
   */
  const restartDownloadAnimation = () => {
    setIsAnimating(false);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsAnimating(true));
    });
  };

  return (
    /*
     * Part of the page from the first paint, so it is read in document order
     * and needs no live region.
     */
    <div className="border-b border-blue-200 bg-blue-50">
      <div
        className={cn(
          "mx-auto flex w-full max-w-5xl items-start gap-3 px-4",
          compact ? "py-2" : "py-3",
        )}
      >
        <Info
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-blue-600"
        />
        <div className="text-sm text-blue-900">
          <p className="font-semibold">
            It looks like your browser may not be able to display this PDF
            correctly.
          </p>
          {compact ? null : (
            <>
              <p className="mt-1">
                For the best experience, we recommend opening the PDF in Google
                Chrome, where you can easily view, zoom, search, and print the
                document.
              </p>
              <p className="mt-1">
                You can also download the PDF and open it using your preferred
                PDF viewer.
              </p>
            </>
          )}
          {/*
            Styled from the shared Button's own variant definition, so it tracks
            the theme, but rendered as an anchor: a download is a navigation,
            and <Button> itself pulls in useActionLogger ->
            useLogger/useLocation, which need a LoggerProvider and a Router that
            the standalone viewer page has neither of.
          */}
          <a
            className={cn(
              buttonVariants({
                variant: "default",
                size: compact ? "sm" : "default",
              }),
              "no-underline",
              compact ? "mt-2" : "mt-3",
            )}
            href={downloadUrl}
            download={downloadFileName}
            onClick={restartDownloadAnimation}
          >
            <Download
              aria-hidden="true"
              className={cn(isAnimating && "animate-bounce-download")}
            />
            Download PDF
          </a>
        </div>
      </div>
    </div>
  );
}
